import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { Stack } from "@mui/material";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1
  },

  extraAttr: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },

  btnWrapper: {
    position: "relative"
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  }
}));

const FlowBuilderIntervalModal = ({
  open,
  onSave,
  data,
  onUpdate,
  close
}) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [timeValue, setTimeValue] = useState(0);
  const [timeUnit, setTimeUnit] = useState("seconds");
  const [activeModal, setActiveModal] = useState(false);
  const [nodeName, setNodeName] = useState("");

  useEffect(() => {
    if (open === 'edit') {
      const totalSeconds = data.data.sec || 0;
      if (totalSeconds > 0 && totalSeconds % 86400 === 0) {
        setTimeValue(totalSeconds / 86400);
        setTimeUnit("days");
      } else if (totalSeconds > 0 && totalSeconds % 3600 === 0) {
        setTimeValue(totalSeconds / 3600);
        setTimeUnit("hours");
      } else if (totalSeconds > 0 && totalSeconds % 60 === 0) {
        setTimeValue(totalSeconds / 60);
        setTimeUnit("minutes");
      } else {
        setTimeValue(totalSeconds);
        setTimeUnit("seconds");
      }
      setNodeName(data.data.name || "");
      setActiveModal(true);
    } else if (open === 'create') {
      setTimeValue(0);
      setTimeUnit("seconds");
      setNodeName("");
      setActiveModal(true);
    }
    return () => {
      isMounted.current = false;
    };
  }, [open]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSaveContact = async values => {
    if (!timeValue || parseFloat(timeValue) <= 0) {
      return toast.error("Por favor, ingrese un valor válido para el tiempo.");
    }
    
    let multiplier = 1;
    if (timeUnit === "minutes") multiplier = 60;
    if (timeUnit === "hours") multiplier = 3600;
    if (timeUnit === "days") multiplier = 86400;
    
    const timerSec = parseInt(parseFloat(timeValue) * multiplier);

    if (timerSec > 2592000) { // 30 days in seconds
      return toast.error("El tiempo máximo permitido es de 30 días.");
    }
    
    if (open === 'edit') {
      onUpdate({
        ...data,
        data: { ...data.data, sec: timerSec, name: nodeName }
      });
    } else if (open === 'create') {
      onSave({
        sec: timerSec,
        name: nodeName
      });
    }
    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog open={activeModal} onClose={handleClose} fullWidth maxWidth="md" scroll="paper">
        <DialogTitle id="form-dialog-title">
          {open === 'create' ? (i18n.t("flowBuilder.interval.addTitle") || `Adicionar um intervalo ao fluxo`) : (i18n.t("flowBuilder.interval.editTitle") || `Editar intervalo`)}
        </DialogTitle>
        <Stack>
          <DialogContent dividers>
            {/* <TextField
              label="Nombre Interno"
              variant="outlined"
              value={nodeName}
              onChange={e => setNodeName(e.target.value)}
              margin="dense"
              fullWidth
              className={classes.textField}
              style={{ width: "95%", marginBottom: "15px" }}
              helperText="Nombre para identificar este nodo en el editor (ej. 'Espera 5s')"
            /> */}
            <div style={{ display: "flex", gap: "10px", width: "95%", alignItems: "center" }}>
              <TextField
                label="Valor de tiempo"
                name="timeValue"
                type="number"
                value={timeValue}
                onChange={(e) => setTimeValue(e.target.value)}
                autoFocus
                variant="outlined"
                InputProps={{ inputProps: { min: 0 } }}
                margin="dense"
                className={classes.textField}
                style={{ flex: 1 }}
              />
              <FormControl variant="outlined" margin="dense" style={{ flex: 1 }}>
                <InputLabel id="time-unit-label">Unidad de Tiempo</InputLabel>
                <Select
                  labelId="time-unit-label"
                  value={timeUnit}
                  onChange={(e) => setTimeUnit(e.target.value)}
                  label="Unidad de Tiempo"
                >
                  <MenuItem value="seconds">Segundos</MenuItem>
                  <MenuItem value="minutes">Minutos</MenuItem>
                  <MenuItem value="hours">Horas</MenuItem>
                  <MenuItem value="days">Días</MenuItem>
                </Select>
              </FormControl>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} variant="outlined" style={{ borderColor: "#ff4d4f", color: "#ff4d4f", textTransform: "none", fontWeight: "bold", borderRadius: "8px" }}>
              {i18n.t("common.cancel") || "Cancelar"}
            </Button>
            <Button 
              type="submit" 
              onClick={() => handleSaveContact()} 
              variant="contained" 
              style={{ backgroundColor: "#7367F0", color: "#fff", textTransform: "none", fontWeight: "bold", borderRadius: "8px", boxShadow: "0 4px 14px 0 rgba(115, 103, 240, 0.39)" }}
            >
              {open === 'create' ? (i18n.t("common.add") || "Añadir") : (i18n.t("common.save") || "Guardar")}
            </Button>
          </DialogActions>
        </Stack>
      </Dialog>
    </div>
  );
};

export default FlowBuilderIntervalModal;
